// @ts-strict-ignore
import { ChannelFragment } from "@dashboard/graphql";
import { Box } from "@saleor/macaw-ui-next";
import { Calendar, Store } from "lucide-react";
import React from "react";

import { DateRange } from "../../models/filters";

type Preset = "last7d" | "last30d" | "last90d" | "last120d";

interface AnalyticsFiltersV2Props {
  channels: ChannelFragment[];
  channelId?: string | null;
  onChangeChannel: (id: string) => void;
  dateRange: DateRange;
  onChangePreset: (preset: Preset) => void;
  preset: Preset;
}

const PRESETS: Array<{ key: Preset; label: string }> = [
  { key: "last7d", label: "7d" },
  { key: "last30d", label: "30d" },
  { key: "last90d", label: "90d" },
  { key: "last120d", label: "120d" },
];

const formatDate = (s: string) => {
  if (!s) return "";

  const d = new Date(s + "T00:00:00Z");

  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const AnalyticsFilters: React.FC<AnalyticsFiltersV2Props> = ({
  channels,
  channelId,
  onChangeChannel,
  onChangePreset,
  preset,
  dateRange,
}) => {
  return (
    <Box display="flex" alignItems="center" gap={3} style={{ flexWrap: "wrap" }}>
      {channels && channels.length > 1 && (
        <Box
          display="inline-flex"
          alignItems="center"
          gap={2}
          paddingX={3}
          paddingY={2}
          borderRadius={3}
          style={{
            background: "rgba(15, 23, 42, 0.04)",
            color: "#374151",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <Store size={14} />
          <select
            value={channelId ?? ""}
            onChange={e => onChangeChannel(e.target.value)}
            style={{
              appearance: "none",
              border: "none",
              background: "transparent",
              fontSize: 13,
              fontWeight: 600,
              color: "#111827",
              cursor: "pointer",
              outline: "none",
              padding: "0 16px 0 0",
            }}
          >
            {channels.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Box>
      )}
      <Box
        display="inline-flex"
        alignItems="center"
        gap={2}
        paddingX={3}
        paddingY={2}
        borderRadius={3}
        style={{
          background: "rgba(15, 23, 42, 0.04)",
          color: "#374151",
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <Calendar size={14} />
        <span>
          {formatDate(dateRange.from)} – {formatDate(dateRange.to)}
        </span>
      </Box>

      <Box
        display="inline-flex"
        alignItems="center"
        padding={1}
        borderRadius={3}
        style={{
          background: "rgba(15, 23, 42, 0.04)",
          gap: 2,
        }}
      >
        {PRESETS.map(p => {
          const active = preset === p.key;

          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onChangePreset(p.key)}
              style={{
                appearance: "none",
                border: "none",
                cursor: "pointer",
                padding: "6px 12px",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                background: active ? "#fff" : "transparent",
                color: active ? "#111827" : "#6B7280",
                boxShadow: active
                  ? "0 1px 2px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.04)"
                  : "none",
                transition: "background 120ms ease, color 120ms ease",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </Box>
    </Box>
  );
};
